import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './llama style/guide.css';

const STEPS = [
  {
    number: '01',
    icon: '🎯',
    title: 'Build Your Profile',
    href: '/profile-builder',
    cta: 'Open Profile Builder',
    accent: 'step-blue',
    summary: 'Tell the AI about your academics, test scores, extracurriculars, and goals. This powers everything else.',
    tips: [
      { icon: '📄', text: 'Upload your transcript or resume as a PDF — the AI reads it directly so you don\'t have to type everything.' },
      { icon: '🌍', text: 'Select your home country first. The questions and analysis are tailored to your grading system and visa context.' },
      { icon: '🎯', text: 'Be specific with scores — write "SAT 1420" or "IELTS 7.5", not just "good scores". Precision = better advice.' },
      { icon: '🔄', text: 'You can reset and redo your profile any time. Update it as your scores improve.' },
    ],
  },
  {
    number: '02',
    icon: '🏫',
    title: 'Find Your Colleges',
    href: '/rankings',
    cta: 'Open College Finder',
    accent: 'step-purple',
    summary: 'Search thousands of US universities with filters that matter — SAT range, tuition, selectivity, state, and size.',
    tips: [
      { icon: '⚡', text: 'Filters auto-load from your profile. If you\'ve set your SAT score, the SAT range is pre-filled for you.' },
      { icon: '♥', text: 'Save colleges to your shortlist with ♡. Aim for a mix — a few reaches, a few matches, a few safeties.' },
      { icon: '🎯', text: 'Use "Analyze Fit" on each saved school. It runs a full gap analysis personalised to your profile.' },
      { icon: '🔀', text: 'Switch the AI model between Gemini and GPT-3.5 in the Fit Analysis modal to get a second opinion.' },
    ],
  },
  {
    number: '03',
    icon: '📋',
    title: 'Understand What They Want',
    href: '/criteria',
    cta: 'Open Admission Criteria',
    accent: 'step-teal',
    summary: 'Every category — grades, tests, essays, visa, financial aid — explained specifically for your home country.',
    tips: [
      { icon: '🗺️', text: 'Your country auto-loads from your profile. Switch it manually if you want to compare expectations.' },
      { icon: '📝', text: 'Read the Essays section carefully — it\'s the most common gap between strong and weak applications.' },
      { icon: '💰', text: 'Check Financial Aid early. Most US schools give little to no need-based aid to internationals — plan scholarships instead.' },
      { icon: '🛂', text: 'The Visa section covers F-1 specifics for your country. Don\'t skip it — requirements vary significantly.' },
    ],
  },
];

const FAQ = [
  {
    q: 'Does my profile data carry over to the College Finder?',
    a: 'Yes. Your SAT score pre-fills the SAT filter, and the Fit Analysis modal reads your full profile automatically.',
  },
  {
    q: 'Is my data stored securely?',
    a: 'Your profile and shortlist are stored locally in your browser (localStorage), tied to your account. Nothing is sent to external servers except to the AI models when you run an analysis.',
  },
  {
    q: 'Can I use GPT instead of Gemini?',
    a: 'Yes — in both the Profile Builder chat and the Fit Analysis modal there\'s a model selector. Switch between Gemini 2.5 Flash and GPT-3.5 Turbo at any time.',
  },
  {
    q: 'How accurate is the gap analysis?',
    a: 'It\'s a strong directional signal, not a guarantee. It uses your full profile + real college data (SAT averages, acceptance rates, tuition) to give honest, specific feedback. Use it to guide your list, not as a final verdict.',
  },
  {
    q: 'Something isn\'t working. Who do I contact?',
    a: 'Email us at reverieonai@aol.com — we\'ll get back to you.',
  },
];

export default function Guide() {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="gd-page">

      {/* ── Hero ── */}
      <section className="gd-hero">
        <div className="gd-hero-inner">
          <p className="gd-eyebrow">Getting started</p>
          <h1 className="gd-title">How to Use Reverieon AI</h1>
          <p className="gd-subtitle">
            Three steps. Fifteen minutes. A complete picture of where you stand and what to do next.
          </p>
        </div>
      </section>

      {/* ── Steps ── */}
      <section className="gd-steps-section">
        <div className="gd-steps-inner">
          {STEPS.map((step, i) => (
            <div key={step.number} className={`gd-step ${step.accent}`}>
              {/* Connector line between steps */}
              {i < STEPS.length - 1 && <div className="gd-connector" />}

              <div className="gd-step-header">
                <div className="gd-step-num">{step.number}</div>
                <div className="gd-step-meta">
                  <span className="gd-step-icon">{step.icon}</span>
                  <h2 className="gd-step-title">{step.title}</h2>
                </div>
                <Link to={step.href} className="gd-step-cta">{step.cta} →</Link>
              </div>

              <p className="gd-step-summary">{step.summary}</p>

              <div className="gd-tips-grid">
                {step.tips.map((tip, j) => (
                  <div key={j} className="gd-tip">
                    <span className="gd-tip-icon">{tip.icon}</span>
                    <p className="gd-tip-text">{tip.text}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Recommended workflow callout ── */}
      <section className="gd-workflow-section">
        <div className="gd-workflow-inner">
          <h2 className="gd-workflow-title">Recommended workflow</h2>
          <div className="gd-workflow-steps">
            <div className="gd-wf-step">
              <span className="gd-wf-num">1</span>
              <span className="gd-wf-label">Complete Profile Builder</span>
            </div>
            <div className="gd-wf-arrow">→</div>
            <div className="gd-wf-step">
              <span className="gd-wf-num">2</span>
              <span className="gd-wf-label">Save 8–12 colleges to your shortlist</span>
            </div>
            <div className="gd-wf-arrow">→</div>
            <div className="gd-wf-step">
              <span className="gd-wf-num">3</span>
              <span className="gd-wf-label">Run Fit Analysis on each saved school</span>
            </div>
            <div className="gd-wf-arrow">→</div>
            <div className="gd-wf-step">
              <span className="gd-wf-num">4</span>
              <span className="gd-wf-label">Read Admission Criteria for your country</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="gd-faq-section">
        <div className="gd-faq-inner">
          <h2 className="gd-faq-title">Frequently asked questions</h2>
          <div className="gd-faq-list">
            {FAQ.map((item, i) => (
              <div
                key={i}
                className={`gd-faq-item${openFaq === i ? ' open' : ''}`}
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
              >
                <div className="gd-faq-q">
                  <span>{item.q}</span>
                  <i className={`fa-solid fa-chevron-${openFaq === i ? 'up' : 'down'}`} />
                </div>
                {openFaq === i && <p className="gd-faq-a">{item.a}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Back nav ── */}
      <div className="gd-back">
        <Link to="/home" className="gd-back-btn">← Back to Dashboard</Link>
      </div>

    </div>
  );
}
